import React, { useState, useEffect, useCallback } from 'react';
import { formatDateCST } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Grid,
  CircularProgress,
  Pagination,
  Collapse,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Science as ScienceIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  PlayArrow as StartIcon,
  Done as DoneIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Inventory as InventoryIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import ReceiveDonorDialog from '../../components/dialogs/ReceiveDonorDialog';
import ProcessTissueDialog from '../../components/dialogs/ProcessTissueDialog';
import ManufactureVialsDialog from '../../components/dialogs/ManufactureVialsDialog';
import DonorEligibilityDialog from '../../components/dialogs/DonorEligibilityDialog';
import DonorDetailDialog from '../../components/dialogs/DonorDetailDialog';
import KpiCard from '../../components/crm/KpiCard';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const getStatusColor = (status) => {
  const colors = {
    'RECEIVED': 'info',
    'PROCESSING': 'warning',
    'PROCESSING_COMPLETE': 'secondary',
    'MANUFACTURED': 'primary',
    'ELIGIBILITY_PENDING': 'warning',
    'ELIGIBLE': 'success',
    'RELEASED': 'success',
    'DISCARDED': 'error'
  };
  return colors[status] || 'default';
};

const getStatusLabel = (status) => {
  const labels = {
    'RECEIVED': 'Received',
    'PROCESSING': 'Processing',
    'PROCESSING_COMPLETE': 'Ready to Manufacture',
    'MANUFACTURED': 'Manufactured',
    'ELIGIBILITY_PENDING': 'Eligibility Pending',
    'ELIGIBLE': 'Eligible',
    'RELEASED': 'Released',
    'DISCARDED': 'Discarded'
  };
  return labels[status] || status;
};

const DonorManagement = ({ currentCompany }) => {
  const { org } = useOrg();
  const navigate = useNavigate();
  const effectiveOrg = (currentCompany || org || 'epl').toLowerCase();

  // State
  const [donors, setDonors] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Processing, 1: QC Testing, 2: Product Release, 3: Completed
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Dialog states
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [manufactureDialogOpen, setManufactureDialogOpen] = useState(false);
  const [eligibilityDialogOpen, setEligibilityDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  
  // KPI Banner states
  const [kpiBannersVisible, setKpiBannersVisible] = useState(() => {
    const stored = localStorage.getItem('donorManagement_kpiBannersVisible');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [kpiBannersEnabled, setKpiBannersEnabled] = useState(() => {
    const stored = localStorage.getItem('donorManagement_kpiBannersEnabled');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [adminSettingsOpen, setAdminSettingsOpen] = useState(false);

  // Tab status mapping - new workflow tabs
  // Processing: RECEIVED, PROCESSING, PROCESSING_COMPLETE, MANUFACTURED (not finalized)
  // QC Testing: Manufacturing finalized but QC not complete (can be MANUFACTURED or ELIGIBILITY_PENDING status)
  // Product Release: QC complete but not released
  // Completed: RELEASED
  const tabConfigs = [
    { 
      name: 'Processing', 
      statuses: ['RECEIVED', 'PROCESSING', 'PROCESSING_COMPLETE'],
      filterType: 'processing' // Status in list OR MANUFACTURED but not finalized
    },
    { 
      name: 'QC Testing', 
      statuses: ['MANUFACTURED', 'ELIGIBILITY_PENDING'],
      filterType: 'qc_testing' // Manufacturing finalized but QC not complete
    },
    { 
      name: 'Product Release', 
      statuses: ['ELIGIBLE', 'ELIGIBILITY_PENDING'],
      filterType: 'product_release' // QC complete but not released
    },
    { 
      name: 'Completed', 
      statuses: ['RELEASED'],
      filterType: 'status' // Released status
    }
  ];

  // Helper function to calculate stats from all donors
  const calculateStats = (allDonors) => {
    const stats = {
      total: allDonors.length,
      processing: 0,
      qcTesting: 0,
      productRelease: 0,
      released: 0
    };

    allDonors.forEach(d => {
      const manufacturingFinalized = d.manufacturingFinalized === true || d.manufacturingFinalized === 1;
      const qcTestingComplete = d.qcTestingComplete === true || d.qcTestingComplete === 1;
      
      // Processing: RECEIVED, PROCESSING, PROCESSING_COMPLETE, or MANUFACTURED but not finalized
      if (['RECEIVED', 'PROCESSING', 'PROCESSING_COMPLETE'].includes(d.status) || 
          (d.status === 'MANUFACTURED' && !manufacturingFinalized)) {
        stats.processing++;
      }
      
      // QC Testing: manufacturing finalized but QC not complete
      if (manufacturingFinalized && !qcTestingComplete && 
          (d.status === 'MANUFACTURED' || d.status === 'ELIGIBILITY_PENDING')) {
        stats.qcTesting++;
      }
      
      // Product Release: QC complete but not released
      if (qcTestingComplete && d.status !== 'RELEASED' && 
          (d.status === 'ELIGIBLE' || d.status === 'ELIGIBILITY_PENDING')) {
        stats.productRelease++;
      }
      
      // Completed: RELEASED
      if (d.status === 'RELEASED') {
        stats.released++;
      }
    });

    return stats;
  };

  // Fetch donors
  const fetchDonors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const tabConfig = tabConfigs[activeTab];
      
      // Fetch all donors for accurate stats calculation (not paginated)
      const statsUrl = `${API_BASE_URL}/donors?limit=1000`;
      const statsResponse = await fetch(statsUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success) {
          const allDonorsForStats = statsData.data || [];
          const calculatedStats = calculateStats(allDonorsForStats);
          setStats(calculatedStats);
        }
      }
      
      // Fetch paginated donors for display
      let url = `${API_BASE_URL}/donors?page=${page}&limit=50`;

      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch donors');
      }

      const data = await response.json();
      
      if (data.success) {
        const allDonors = data.data || [];
        
        // Client-side filtering based on tab configuration
        let filteredDonors = allDonors;
        if (tabConfig) {
          if (tabConfig.filterType === 'processing') {
            // Filter: Status in list OR MANUFACTURED but not finalized
            filteredDonors = allDonors.filter(d => {
              const manufacturingFinalized = d.manufacturingFinalized === true || d.manufacturingFinalized === 1;
              return tabConfig.statuses.includes(d.status) || 
                     (d.status === 'MANUFACTURED' && !manufacturingFinalized);
            });
          } else if (tabConfig.filterType === 'qc_testing') {
            // Filter: manufacturing finalized but QC not complete (can be MANUFACTURED or ELIGIBILITY_PENDING status)
            filteredDonors = allDonors.filter(d => {
              const manufacturingFinalized = d.manufacturingFinalized === true || d.manufacturingFinalized === 1;
              const qcTestingComplete = d.qcTestingComplete === true || d.qcTestingComplete === 1;
              return manufacturingFinalized && 
                     !qcTestingComplete && 
                     (d.status === 'MANUFACTURED' || d.status === 'ELIGIBILITY_PENDING');
            });
          } else if (tabConfig.filterType === 'product_release') {
            // Filter: QC complete but not released
            filteredDonors = allDonors.filter(d => {
              const qcTestingComplete = d.qcTestingComplete === true || d.qcTestingComplete === 1;
              return qcTestingComplete && 
                     d.status !== 'RELEASED' && 
                     (d.status === 'ELIGIBLE' || d.status === 'ELIGIBILITY_PENDING');
            });
          } else if (tabConfig.filterType === 'status') {
            // Filter by status array
            filteredDonors = allDonors.filter(d => 
              tabConfig.statuses.includes(d.status)
            );
          }
        }
        
        setDonors(filteredDonors);
        setTotalPages(data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching donors:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, searchTerm]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
  };

  // Handle donor received
  const handleDonorReceived = () => {
    setReceiveDialogOpen(false);
    fetchDonors();
  };

  // Handle action buttons
  const handleStartProcessing = async (donor) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/start-processing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start processing');
      }

      fetchDonors();
    } catch (err) {
      console.error('Error starting processing:', err);
      setError(err.message);
    }
  };

  const handleProcessTissue = (donor) => {
    setSelectedDonor(donor);
    setProcessDialogOpen(true);
  };

  const handleManufacture = (donor) => {
    setSelectedDonor(donor);
    setManufactureDialogOpen(true);
  };

  const handleOpenEligibility = (donor) => {
    setSelectedDonor(donor);
    setEligibilityDialogOpen(true);
  };

  const handleViewDetails = (donor) => {
    navigate(`/internal/${effectiveOrg}/donor-management/${donor.id}`);
  };

  // KPI Banner handlers
  const handleToggleKpiBanners = () => {
    const newValue = !kpiBannersVisible;
    setKpiBannersVisible(newValue);
    localStorage.setItem('donorManagement_kpiBannersVisible', JSON.stringify(newValue));
  };

  const handleToggleKpiBannersEnabled = () => {
    const newValue = !kpiBannersEnabled;
    setKpiBannersEnabled(newValue);
    localStorage.setItem('donorManagement_kpiBannersEnabled', JSON.stringify(newValue));
    // If disabling, also hide the banners
    if (!newValue) {
      setKpiBannersVisible(false);
      localStorage.setItem('donorManagement_kpiBannersVisible', JSON.stringify(false));
    }
  };

  // Check if user is admin (simplified check - can be enhanced with proper auth check)
  const isAdmin = () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return currentUser.userType === 'INTERNAL' && 
             (currentUser.permissions?.includes('admin.settings') || 
              currentUser.email?.includes('admin') ||
              currentUser.email?.includes('manager'));
    } catch {
      return false;
    }
  };


  // Render action buttons - Only View Details button now
  // All processing actions moved to detail page for better workflow control
  const renderActions = (donor) => {
    return (
      <Tooltip title="View Details">
        <IconButton 
          color="primary" 
          size="small"
          onClick={() => handleViewDetails(donor)}
        >
          <ViewIcon />
        </IconButton>
      </Tooltip>
    );
  };

  // Only show for EPL
  if (effectiveOrg !== 'epl') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Donor Management is only available for EPL organization.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs 
        items={[
          { label: 'EPL Internal', path: `/internal/epl/dashboard` },
          { label: 'Donor Management' }
        ]} 
      />

      {/* Header Banner */}
      <Box sx={{
        mb: 3,
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        boxShadow: '0 4px 12px rgba(3, 149, 130, 0.3)'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Donor Management - EPL
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Receive and process donor tissue for EPL products
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchDonors}
            sx={{
              bgcolor: 'white',
              color: '#039582',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setReceiveDialogOpen(true)}
            sx={{
              bgcolor: 'white',
              color: '#039582',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Receive Donor
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPI Banners */}
      {kpiBannersEnabled && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Key Performance Indicators
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={kpiBannersVisible ? "Hide KPI Banners" : "Show KPI Banners"}>
                <IconButton
                  onClick={handleToggleKpiBanners}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  {kpiBannersVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </Tooltip>
              {isAdmin() && (
                <Tooltip title="Admin Settings">
                  <IconButton
                    onClick={() => setAdminSettingsOpen(true)}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          
          <Collapse in={kpiBannersVisible}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(5, 1fr)'
                },
                gap: 3
              }}
            >
              <KpiCard
                title="Total Donors"
                value={stats.total || 0}
                subtitle="all time"
                icon={<PeopleIcon />}
                color="#039582"
              />
              <KpiCard
                title="Processing"
                value={stats.processing || 0}
                subtitle="in progress"
                icon={<ScienceIcon />}
                color="#2196f3"
              />
              <KpiCard
                title="QC Testing"
                value={stats.qcTesting || 0}
                subtitle="awaiting testing"
                icon={<AssessmentIcon />}
                color="#ff9800"
              />
              <KpiCard
                title="Product Release"
                value={stats.productRelease || 0}
                subtitle="ready for release"
                icon={<TimelineIcon />}
                color="#9c27b0"
              />
              <KpiCard
                title="Completed"
                value={stats.released || 0}
                subtitle="released"
                icon={<CheckIcon />}
                color="#4caf50"
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Tabs and Search */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={`Processing (${stats.processing || 0})`} />
            <Tab label={`QC Testing (${stats.qcTesting || 0})`} />
            <Tab label={`Product Release (${stats.productRelease || 0})`} />
            <Tab label={`Completed (${stats.released || 0})`} />
          </Tabs>
          <TextField
            size="small"
            placeholder="Search donors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: 250 }}
          />
        </Box>

        {/* Loading */}
        {loading && <LinearProgress />}

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Donor ID</strong></TableCell>
                <TableCell><strong>Mare Name</strong></TableCell>
                <TableCell><strong>Date Received</strong></TableCell>
                <TableCell><strong>Tissue Types</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Vials Made</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && donors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No donors found for this category.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                donors.map((donor) => (
                  <TableRow key={donor.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {donor.donorId}
                      </Typography>
                    </TableCell>
                    <TableCell>{donor.mareName}</TableCell>
                    <TableCell>
                      {formatDateCST(donor.dateReceived)}
                    </TableCell>
                    <TableCell>
                      {donor.tissues?.map((t) => (
                        <Chip 
                          key={t.id} 
                          label={t.tissueType} 
                          size="small" 
                          sx={{ mr: 0.5 }}
                          color={t.tissueType === 'AM' ? 'primary' : 'secondary'}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(donor.status)} 
                        size="small" 
                        color={getStatusColor(donor.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Calculate from manufacturedVials array if available, otherwise use totalVialsMade
                        if (donor.manufacturedVials && donor.manufacturedVials.length > 0) {
                          const total = donor.manufacturedVials.reduce((sum, vial) => sum + (vial.quantity || 0), 0);
                          return total > 0 ? <strong style={{ color: '#16a34a' }}>{total}</strong> : '-';
                        }
                        return donor.totalVialsMade || '-';
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {renderActions(donor)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <ReceiveDonorDialog 
        open={receiveDialogOpen}
        onClose={() => setReceiveDialogOpen(false)}
        onSuccess={handleDonorReceived}
      />

      <ProcessTissueDialog 
        open={processDialogOpen}
        onClose={() => setProcessDialogOpen(false)}
        donor={selectedDonor}
        onSuccess={() => {
          setProcessDialogOpen(false);
          fetchDonors();
        }}
      />

      <ManufactureVialsDialog 
        open={manufactureDialogOpen}
        onClose={() => setManufactureDialogOpen(false)}
        donor={selectedDonor}
        onSuccess={() => {
          setManufactureDialogOpen(false);
          fetchDonors();
        }}
      />

      <DonorEligibilityDialog 
        open={eligibilityDialogOpen}
        onClose={() => setEligibilityDialogOpen(false)}
        donor={selectedDonor}
        onSuccess={() => {
          setEligibilityDialogOpen(false);
          fetchDonors();
        }}
      />

      <DonorDetailDialog 
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        donor={selectedDonor}
      />

      {/* Admin Settings Dialog */}
      <Dialog 
        open={adminSettingsOpen} 
        onClose={() => setAdminSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          KPI Banner Settings
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={kpiBannersEnabled}
                  onChange={handleToggleKpiBannersEnabled}
                  color="primary"
                />
              }
              label="Enable KPI Banners"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
              When disabled, KPI banners will be hidden for all users. This is a global setting.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminSettingsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DonorManagement;

